import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import UserChip from 'studio/components/Chip/UserChip/UserChip';
import RoleChip from 'studio/components/Chip/RoleChip/RoleChip';

const styles = theme => ({
  container: {
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
    position: 'relative',
    overflow: 'hidden'
  },
  itemsContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    flexGrow: 1,
    minWidth: 0,
    overflow: 'hidden'
  },
  measureContainer: {
    position: 'absolute',
    visibility: 'hidden',
    display: 'flex',
    gap: theme.spacing(1)
  },
  overflowChip: {
    flexShrink: 0,
    minWidth: 'auto',
    cursor: 'pointer'
  },
  popoverContent: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxHeight: 400,
    maxWidth: 300,
    overflow: 'auto'
  }
});

const OVERFLOW_CHIP_WIDTH = 60;

const SharingDetails = ({ classes, sharedBoard, roles, users }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const containerRef = useRef(null);
  const measureRef = useRef(null);

  const allItems = useMemo(() => {
    if (!sharedBoard) return [];

    const items = [];
    const { isSharedWithOrg, roleIds = [], userIds = [] } = sharedBoard;

    if (isSharedWithOrg) {
      items.push({
        type: 'org',
        key: 'org',
        element: (
          <Tooltip title="Shared with entire organization">
            <Chip label="Organization" size="small" color="primary" />
          </Tooltip>
        )
      });
    }

    roleIds.forEach(roleId => {
      items.push({
        type: 'role',
        key: roleId,
        element: <RoleChip key={roleId} role={roles?.find(r => r.id === roleId)} roleId={roleId} size="small" />
      });
    });

    userIds.forEach(userId => {
      items.push({
        type: 'user',
        key: userId,
        element: <UserChip key={userId} user={users?.find(u => u.id === userId)} userId={userId} size="small" />
      });
    });

    return items;
  }, [sharedBoard, roles, users]);

  useEffect(() => {
    const updateVisibleItems = () => {
      if (!containerRef.current || !measureRef.current || allItems.length === 0) return;

      const containerWidth = containerRef.current.offsetWidth;
      const availableWidth = containerWidth - OVERFLOW_CHIP_WIDTH;

      // Render all items in the measure container
      const measureContainer = measureRef.current;
      measureContainer.style.width = `${availableWidth}px`;

      // Find how many items fit
      const itemElements = Array.from(measureContainer.children);
      let currentWidth = 0;
      let count = 0;

      for (const item of itemElements) {
        const itemWidth = item.offsetWidth;
        if (currentWidth + itemWidth <= availableWidth) {
          currentWidth += itemWidth;
          count++;
        } else {
          break;
        }
      }

      setVisibleCount(count);
    };

    const resizeObserver = new ResizeObserver(updateVisibleItems);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      updateVisibleItems();
    }

    return () => resizeObserver.disconnect();
  }, [allItems]);

  if (!sharedBoard) {
    return '-';
  }

  const visibleItems = allItems.slice(0, visibleCount);
  const hiddenItems = allItems.slice(visibleCount);

  const handleOverflowClick = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className={classes.container} ref={containerRef}>
      <div className={classes.itemsContainer}>
        {visibleItems.map(item => (
          <div key={`visible-${item.type}-${item.key}`}>{item.element}</div>
        ))}
      </div>

      <div className={classes.measureContainer} ref={measureRef}>
        {allItems.map(item => (
          <div key={`measure-${item.type}-${item.key}`}>{item.element}</div>
        ))}
      </div>

      {hiddenItems.length > 0 && (
        <>
          <Chip
            label={`+${hiddenItems.length}`}
            size="small"
            className={classes.overflowChip}
            onClick={handleOverflowClick}
          />
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
          >
            <div className={classes.popoverContent}>
              {allItems.map(item => (
                <div key={`popover-${item.type}-${item.key}`}>{item.element}</div>
              ))}
            </div>
          </Popover>
        </>
      )}
    </div>
  );
};

SharingDetails.propTypes = {
  classes: PropTypes.object.isRequired,
  sharedBoard: PropTypes.shape({
    isSharedWithOrg: PropTypes.bool,
    roleIds: PropTypes.arrayOf(PropTypes.string),
    userIds: PropTypes.arrayOf(PropTypes.string)
  }),
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired
    })
  ),
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired
    })
  )
};

export default withStyles(styles)(SharingDetails);
